import React, { useState } from "react";
import axios from "axios";
import {
  apiService,
  IBulkHolidayUploadError,
  IBulkHolidayUploadResult,
} from "../../services/api";
import GenericBulkUploadModal, {
  BulkUploadResultPayload,
} from "../common/GenericBulkUploadModal";
import toast from "react-hot-toast";

interface HolidayBulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface BackendErrorBody {
  message?: string | string[];
  error?: string;
}

const HolidayBulkUploadModal: React.FC<HolidayBulkUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUploadResultPayload | null>(null);

  const generateCSVTemplate = () => {
    const currentYear = new Date().getFullYear();
    const headers = [
      "Holiday Name",
      "Date (YYYY-MM-DD)",
      "Year",
      "Type (mandatory/optional)",
      "Description",
    ];

    const sampleData = [
      "New Year's Day",
      `${currentYear}-01-01`,
      `${currentYear}`,
      "mandatory",
      "New Year celebration",
    ];

    const csvContent = [headers.join(","), sampleData.join(",")].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "holiday_bulk_upload_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success("Holiday CSV template downloaded successfully.");
  };

  const extractErrorMessage = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
      const body = error.response?.data as BackendErrorBody | undefined;
      if (body?.message) {
        return Array.isArray(body.message) ? body.message.join(", ") : body.message;
      }
      if (body?.error) return body.error;
      return error.message || "Upload failed";
    }
    if (error instanceof Error) return error.message;
    return "Upload failed";
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const stats: IBulkHolidayUploadResult = await apiService.bulkUploadHolidays(formData);
      const hasImports = stats.successfulImports > 0;

      const resultPayload: BulkUploadResultPayload = {
        success: hasImports,
        message:
          stats.summary ||
          (hasImports
            ? `Successfully imported ${stats.successfulImports} out of ${stats.totalRecords} records.`
            : "No records were imported successfully."),
        stats: {
          total: stats.totalRecords,
          success: stats.successfulImports,
          failed: stats.failedImports,
        },
        duplicates: stats.duplicates,
        inlineErrors: stats.errors?.map((err: IBulkHolidayUploadError) => ({
          row: err.row,
          field: err.field,
          message: err.message,
        })),
      };

      setUploadResult(resultPayload);

      if (hasImports) {
        toast.success(resultPayload.message);
        onSuccess();
      } else {
        toast.error(resultPayload.message);
      }
    } catch (error) {
      console.error("Upload error:", error);
      const errorMessage = extractErrorMessage(error);

      setUploadResult({
        success: false,
        message: errorMessage,
      });

      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleModalClose = () => {
    setUploadResult(null);
    onClose();
  };

  const instructions = [
    "Holiday names must be unique for the same date and year.",
    "Dates must be in YYYY-MM-DD format.",
    "Year must be a valid number between 1900 and 2100.",
    "Type must be either 'mandatory' or 'optional'.",
    "Description is optional.",
    "Duplicate records will be reported and skipped automatically.",
    "Invalid rows will be displayed with specific field error details.",
  ];

  return (
    <GenericBulkUploadModal
      isOpen={isOpen}
      onClose={handleModalClose}
      title="Bulk Upload Holidays"
      subtitle="Import company holidays in bulk using a structured CSV file"
      acceptedExtensions={[".csv"]}
      fileTypeLabel="CSV Document (.csv)"
      templateConfig={{
        title: "Step 1: Download Holiday Template",
        description:
          "Pre-formatted CSV structure with sample row and field definitions.",
        buttonLabel: "Download Template",
        onDownload: generateCSVTemplate,
      }}
      isUploading={isUploading}
      onUpload={handleFileUpload}
      instructions={instructions}
      result={uploadResult}
      onResetResult={() => setUploadResult(null)}
      onSuccessClose={onSuccess}
      width={720}
    />
  );
};

export default HolidayBulkUploadModal;
